import { useState } from 'react';
import { useCreateInteraction } from '../api';
import { INTERACTION_TYPES, formatInteractionType } from '../interactionTypes';

function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface Props {
  personIds?: string[];
  householdIds?: string[];
  onDone: () => void;
  onCancel: () => void;
}

export default function LogInteractionForm({ personIds, householdIds, onDone, onCancel }: Props) {
  const [date, setDate] = useState(todayLocal());
  const [type, setType] = useState('visit');
  const [notes, setNotes] = useState('');
  const [performedBy, setPerformedBy] = useState('');
  const create = useCreateInteraction();

  const submit = () => {
    if (!date || !type) return;
    create.mutate(
      {
        occurred_at: date,
        type,
        notes: notes.trim() || undefined,
        performed_by: performedBy.trim() || undefined,
        person_ids: personIds,
        household_ids: householdIds,
      },
      { onSuccess: onDone },
    );
  };

  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      <div className="border-b border-slate-200 dark:border-slate-700 px-3 py-1 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Log Interaction
      </div>
      <div className="space-y-2 px-3 py-2">
        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-0.5 text-xs text-slate-600 dark:text-slate-400">
            Date
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
            />
          </label>
          <label className="flex flex-col gap-0.5 text-xs text-slate-600 dark:text-slate-400">
            Type
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
            >
              {INTERACTION_TYPES.map((t) => (
                <option key={t} value={t}>{formatInteractionType(t)}</option>
              ))}
            </select>
          </label>
        </div>
        <label className="flex flex-col gap-0.5 text-xs text-slate-600 dark:text-slate-400">
          Performed by (optional)
          <input
            value={performedBy}
            onChange={(e) => setPerformedBy(e.target.value)}
            placeholder="Leave blank if it was you"
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
          />
        </label>
        <label className="flex flex-col gap-0.5 text-xs text-slate-600 dark:text-slate-400">
          Notes (optional)
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What happened?"
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
          />
        </label>
        {create.error && (
          <p className="text-xs text-rose-700 dark:text-rose-300">Save failed: {(create.error as Error).message}</p>
        )}
        <div className="flex gap-1 justify-end">
          <button
            onClick={submit}
            disabled={create.isPending || !date || !type}
            className="rounded bg-slate-700 dark:bg-slate-600 px-3 py-1 text-xs text-white disabled:opacity-50"
          >
            {create.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            onClick={onCancel}
            disabled={create.isPending}
            className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1 text-xs text-slate-600 dark:text-slate-400"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
